import React, { useCallback, useEffect, useState } from "react";
import {
    odometerApi,
    OdometerApiError,
    OdometerReading,
    OdometerVehicleStatus,
} from "../../data/odometerApi";

interface OdometerHistoryPanelProps {
    vehicle: OdometerVehicleStatus | null;
    /** 저장이 일어나면 값이 바뀌어 이력을 다시 불러온다. */
    refreshToken: number;
    onClose: () => void;
}

const formatBytes = (bytes: number | null): string => {
    if (bytes === null) {
        return "-";
    }
    return bytes >= 1024 * 1024
        ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
        : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

/** 직전 행과의 증감(km). 이력은 최신순이므로 다음 인덱스가 더 과거다. */
const deltaFrom = (readings: OdometerReading[], index: number): number | null => {
    const previous = readings[index + 1];
    return previous ? readings[index].odometerKm - previous.odometerKm : null;
};

export default function OdometerHistoryPanel({
    vehicle,
    refreshToken,
    onClose,
}: OdometerHistoryPanelProps) {
    const [readings, setReadings] = useState<OdometerReading[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const loadReadings = useCallback(async (vin: string) => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await odometerApi.listReadings(vin);
            setReadings(response.readings);
        } catch (error) {
            setReadings([]);
            setErrorMessage(
                error instanceof OdometerApiError ? error.message : "이력을 불러오지 못했습니다."
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!vehicle) {
            setReadings([]);
            return;
        }
        loadReadings(vehicle.vin);
    }, [vehicle, refreshToken, loadReadings]);

    const isOpen = Boolean(vehicle);

    return (
        <>
            <div className={`vm-backdrop ${isOpen ? "open" : ""}`} onClick={onClose}></div>
            <aside className={`vm-form-container ${isOpen ? "open" : ""}`}>
                {vehicle && (
                    <>
                        <div className="odo-panel-header">
                            <div>
                                <h3 className="vm-form-title" style={{ margin: 0 }}>
                                    주행거리 입력 이력
                                </h3>
                                <div className="odo-modal-subtitle">
                                    {vehicle.vehicleNumber}
                                    {vehicle.model ? ` · ${vehicle.model}` : ""}
                                </div>
                            </div>
                            <button type="button" className="odo-close-btn" onClick={onClose} aria-label="닫기">
                                &times;
                            </button>
                        </div>

                        <div className="odo-panel-summary">
                            <div className="odo-summary-item">
                                <span className="odo-summary-label">현재 누적거리</span>
                                <span className="odo-summary-value">
                                    {vehicle.previousKm.toLocaleString()} km
                                </span>
                            </div>
                            <div className="odo-summary-item">
                                <span className="odo-summary-label">입력 주기</span>
                                <span className="odo-summary-value">{vehicle.cycleLabel}</span>
                            </div>
                            <div className="odo-summary-item">
                                <span className="odo-summary-label">다음 예정일</span>
                                <span className="odo-summary-value">{vehicle.nextDueDate || "-"}</span>
                            </div>
                        </div>

                        {isLoading && <div className="odo-panel-state">이력을 불러오는 중입니다...</div>}
                        {errorMessage && <div className="odo-banner error">{errorMessage}</div>}

                        {!isLoading && !errorMessage && readings.length === 0 && (
                            <div className="odo-panel-state">아직 입력된 주행거리 이력이 없습니다.</div>
                        )}

                        {readings.length > 0 && (
                            <div className="odo-history-list">
                                {readings.map((reading, index) => {
                                    const delta = deltaFrom(readings, index);
                                    return (
                                        <div className="odo-history-item" key={reading.readingId}>
                                            <div className="odo-history-top">
                                                <span className="odo-history-date">{reading.measuredAt}</span>
                                                <span className="odo-history-km">
                                                    {reading.odometerKm.toLocaleString()} km
                                                </span>
                                            </div>
                                            <div className="odo-history-meta">
                                                <span
                                                    className={`odo-tag ${
                                                        reading.inputMethod === "OCR_ASSISTED" ? "ocr" : "manual"
                                                    }`}
                                                >
                                                    {reading.inputMethod === "OCR_ASSISTED"
                                                        ? "사진 인식"
                                                        : "직접 입력"}
                                                </span>
                                                {delta !== null && (
                                                    <span className="odo-history-delta">
                                                        +{delta.toLocaleString()} km
                                                    </span>
                                                )}
                                                <span className="odo-history-user">
                                                    {reading.registeredByName}
                                                </span>
                                            </div>

                                            {reading.photoFileName && (
                                                <div className="odo-history-detail">
                                                    사진 {reading.photoFileName} (
                                                    {formatBytes(reading.photoSizeBytes)}, {reading.photoMimeType})
                                                    — 원본 미보관
                                                </div>
                                            )}
                                            {reading.ocrCandidateKm !== null && (
                                                <div className="odo-history-detail">
                                                    OCR 인식값 {reading.ocrCandidateKm.toLocaleString()} km
                                                    {reading.ocrConfidence !== null
                                                        ? ` · 신뢰도 ${Math.round(reading.ocrConfidence * 100)}%`
                                                        : ""}
                                                    {reading.ocrCandidateKm !== reading.odometerKm
                                                        ? " · 사용자가 수정함"
                                                        : ""}
                                                </div>
                                            )}
                                            {reading.memo && (
                                                <div className="odo-history-detail">메모: {reading.memo}</div>
                                            )}
                                            <div className="odo-history-created">{reading.createdDate} 등록</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </aside>
        </>
    );
}
